import { useRef } from "react";
import { Button } from "../components/ui/button";
import { Printer, Share2, Copy, Check } from "lucide-react";
import toast from "react-hot-toast";
import { useState } from "react";

// دالة لإنشاء رقم تقرير فريد
export const generateReportId = () => {
  const timestamp = Date.now().toString(36).toUpperCase();
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `RPT-${timestamp}-${random}`;
};

// مكون أزرار الطباعة والمشاركة
export const PrintShareButtons = ({ 
  onPrint, 
  onShare, 
  reportId,
  language = "ar"
}) => {
  const [copied, setCopied] = useState(false);

  const copyReportId = async () => {
    try {
      await navigator.clipboard.writeText(reportId);
      setCopied(true);
      toast.success(language === "ar" ? "تم نسخ رقم التقرير" : "Report ID copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(language === "ar" ? "فشل النسخ" : "Copy failed");
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {reportId && (
        <div className="flex items-center gap-2 bg-slate-700/50 px-3 py-1.5 rounded-lg">
          <span className="text-slate-400 text-xs">{language === "ar" ? "رقم التقرير:" : "Report ID:"}</span>
          <span className="text-white font-mono text-sm">{reportId}</span>
          <Button
            size="sm"
            variant="ghost"
            onClick={copyReportId}
            className="h-6 w-6 p-0 text-slate-400 hover:text-white"
          >
            {copied ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
          </Button>
        </div>
      )}
      <Button
        size="sm"
        variant="outline"
        onClick={onPrint}
        className="border-slate-600 text-slate-300 hover:bg-slate-700"
      >
        <Printer className="w-4 h-4 me-1" />
        {language === "ar" ? "طباعة" : "Print"}
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={onShare}
        className="border-slate-600 text-slate-300 hover:bg-slate-700"
      >
        <Share2 className="w-4 h-4 me-1" />
        {language === "ar" ? "مشاركة" : "Share"}
      </Button>
    </div>
  );
};

// دالة طباعة التقرير
export const printReport = (title, content, reportId, shopInfo = {}) => {
  const printWindow = window.open("", "_blank");
  if (!printWindow) {
    toast.error("فشل فتح نافذة الطباعة");
    return;
  }

  const currentDate = new Date().toLocaleDateString("ar-IQ", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });

  printWindow.document.write(`
    <!DOCTYPE html>
    <html dir="rtl" lang="ar">
    <head>
      <title>${title}</title>
      <style>
        @page { size: A4; margin: 1cm; }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
          font-family: 'Segoe UI', Tahoma, Arial, sans-serif;
          padding: 20px;
          color: #1e293b;
          line-height: 1.6;
        }
        .header {
          text-align: center;
          border-bottom: 3px solid #10b981;
          padding-bottom: 15px;
          margin-bottom: 20px;
        }
        .header h1 {
          color: #10b981;
          font-size: 24px;
          margin-bottom: 5px;
        }
        .header .shop-name {
          font-size: 18px;
          color: #334155;
        }
        .header .report-info {
          font-size: 12px;
          color: #64748b;
          margin-top: 10px;
        }
        .report-id {
          display: inline-block;
          background: #f1f5f9;
          padding: 5px 15px;
          border-radius: 20px;
          font-family: monospace;
          font-size: 14px;
          color: #334155;
          margin: 10px 0;
        }
        .content {
          margin-bottom: 20px;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin: 15px 0;
        }
        th, td {
          border: 1px solid #e2e8f0;
          padding: 10px;
          text-align: right;
        }
        th {
          background: #f8fafc;
          color: #334155;
          font-weight: bold;
        }
        tr:nth-child(even) { background: #f8fafc; }
        .summary-card {
          display: inline-block;
          background: #f0fdf4;
          border: 1px solid #10b981;
          border-radius: 10px;
          padding: 15px 25px;
          margin: 5px;
          text-align: center;
        }
        .summary-card .label { color: #64748b; font-size: 12px; }
        .summary-card .value { color: #10b981; font-size: 20px; font-weight: bold; }
        .footer {
          text-align: center;
          border-top: 1px solid #e2e8f0;
          padding-top: 15px;
          margin-top: 20px;
          color: #94a3b8;
          font-size: 11px;
        }
        .barcode {
          text-align: center;
          margin: 15px 0;
          font-family: 'Libre Barcode 39', monospace;
          font-size: 40px;
        }
        @media print {
          body { padding: 0; }
        }
      </style>
      <link href="https://fonts.googleapis.com/css2?family=Libre+Barcode+39&display=swap" rel="stylesheet">
    </head>
    <body>
      <div class="header">
        <h1>📊 ${title}</h1>
        ${shopInfo.name ? `<p class="shop-name">${shopInfo.name}</p>` : ''}
        <div class="report-id">${reportId}</div>
        <p class="report-info">تاريخ التقرير: ${currentDate}</p>
      </div>
      <div class="barcode">*${reportId}*</div>
      <div class="content">
        ${content}
      </div>
      <div class="footer">
        <p>حساباتي - نظام إدارة حسابات المتاجر</p>
        ${shopInfo.phone ? `<p>للتواصل: ${shopInfo.phone}</p>` : ''}
      </div>
      <script>
        window.onload = () => {
          setTimeout(() => window.print(), 500);
        };
      </script>
    </body>
    </html>
  `);
  printWindow.document.close();
};

// دالة مشاركة التقرير عبر واتساب
export const shareReport = (title, summary, reportId, language = "ar") => {
  const message = language === "ar" 
    ? `📊 تقرير: ${title}

🔢 رقم التقرير: ${reportId}

${summary}

📅 تاريخ: ${new Date().toLocaleDateString("ar-IQ")}

---
تم إنشاؤه بواسطة حساباتي`
    : `📊 Report: ${title}

🔢 Report ID: ${reportId}

${summary}

📅 Date: ${new Date().toLocaleDateString("en-US")}

---
Generated by Hesabaty`;

  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
  window.open(whatsappUrl, "_blank");
};

// دالة تحويل البيانات إلى HTML للطباعة
export const formatReportContent = (report, reportType, language = "ar") => {
  if (!report) return "";
  
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("ar-IQ", {
      style: "decimal",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0) + " د.ع";
  };

  let html = `
    <div style="display: flex; flex-wrap: wrap; justify-content: center; margin-bottom: 20px;">
      <div class="summary-card">
        <div class="label">${language === "ar" ? "المبيعات" : "Sales"}</div>
        <div class="value">${formatCurrency(report.sales?.total || 0)}</div>
      </div>
      <div class="summary-card">
        <div class="label">${language === "ar" ? "المشتريات" : "Purchases"}</div>
        <div class="value">${formatCurrency(report.purchases?.total || 0)}</div>
      </div>
      <div class="summary-card">
        <div class="label">${language === "ar" ? "المصروفات" : "Expenses"}</div>
        <div class="value">${formatCurrency(report.expenses?.total || 0)}</div>
      </div>
      ${report.salaries?.total > 0 ? `
      <div class="summary-card" style="background: #faf5ff; border-color: #a855f7;">
        <div class="label">${language === "ar" ? "الرواتب" : "Salaries"}</div>
        <div class="value" style="color: #a855f7;">${formatCurrency(report.salaries.total)}</div>
      </div>
      ` : ''}
      <div class="summary-card" style="background: ${report.net_profit >= 0 ? '#f0fdf4' : '#fef2f2'}; border-color: ${report.net_profit >= 0 ? '#10b981' : '#ef4444'};">
        <div class="label">${language === "ar" ? "صافي الربح" : "Net Profit"}</div>
        <div class="value" style="color: ${report.net_profit >= 0 ? '#10b981' : '#ef4444'};">${formatCurrency(report.net_profit || 0)}</div>
      </div>
    </div>
  `;

  // جدول التفاصيل
  html += `
    <table>
      <thead>
        <tr>
          <th>${language === "ar" ? "البند" : "Item"}</th>
          <th>${language === "ar" ? "العدد" : "Count"}</th>
          <th>${language === "ar" ? "المبلغ" : "Amount"}</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>${language === "ar" ? "المبيعات" : "Sales"}</td>
          <td>${report.sales?.count || 0}</td>
          <td>${formatCurrency(report.sales?.total || 0)}</td>
        </tr>
        <tr>
          <td>${language === "ar" ? "المشتريات" : "Purchases"}</td>
          <td>${report.purchases?.count || 0}</td>
          <td>${formatCurrency(report.purchases?.total || 0)}</td>
        </tr>
        <tr>
          <td>${language === "ar" ? "المصروفات" : "Expenses"}</td>
          <td>${report.expenses?.count || 0}</td>
          <td>${formatCurrency(report.expenses?.total || 0)}</td>
        </tr>
        <tr>
          <td>${language === "ar" ? "التالفات" : "Damages"}</td>
          <td>${report.damages?.count || 0}</td>
          <td>${formatCurrency(report.damages?.total || 0)}</td>
        </tr>
        ${report.salaries?.total > 0 ? `
        <tr style="background: #faf5ff;">
          <td style="color: #a855f7; font-weight: bold;">${language === "ar" ? "الرواتب" : "Salaries"}</td>
          <td>-</td>
          <td style="color: #a855f7; font-weight: bold;">${formatCurrency(report.salaries.total)}</td>
        </tr>
        ` : ''}
        <tr style="background: #f0fdf4; font-weight: bold;">
          <td colspan="2">${language === "ar" ? "صافي الربح" : "Net Profit"}</td>
          <td style="color: ${report.net_profit >= 0 ? '#10b981' : '#ef4444'};">${formatCurrency(report.net_profit || 0)}</td>
        </tr>
      </tbody>
    </table>
  `;

  return html;
};

// دالة إنشاء ملخص للمشاركة
export const generateReportSummary = (report, language = "ar") => {
  if (!report) return "";
  
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("ar-IQ").format(value || 0) + " د.ع";
  };

  if (language === "ar") {
    let summary = `💰 المبيعات: ${formatCurrency(report.sales?.total || 0)}
🛒 المشتريات: ${formatCurrency(report.purchases?.total || 0)}
💸 المصروفات: ${formatCurrency(report.expenses?.total || 0)}`;
    
    if (report.salaries?.total > 0) {
      summary += `\n👥 الرواتب: ${formatCurrency(report.salaries.total)}`;
    }
    
    summary += `\n📈 صافي الربح: ${formatCurrency(report.net_profit || 0)}`;
    return summary;
  }
  
  let summary = `💰 Sales: ${formatCurrency(report.sales?.total || 0)}
🛒 Purchases: ${formatCurrency(report.purchases?.total || 0)}
💸 Expenses: ${formatCurrency(report.expenses?.total || 0)}`;
  
  if (report.salaries?.total > 0) {
    summary += `\n👥 Salaries: ${formatCurrency(report.salaries.total)}`;
  }
  
  summary += `\n📈 Net Profit: ${formatCurrency(report.net_profit || 0)}`;
  return summary;
};
